import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  Download,
  FileText,
  FolderOpen,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  deposerDocument,
  fetchDocuments,
  modifierDocument,
  supprimerDocument,
  type DocumentAssociation,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { vide } from '../utils/listes';

const jourCourt = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const poids = (octets: number): string => {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
};

/**
 * La bibliothèque de l'association.
 *
 * Les statuts dormaient dans la boîte mail du président de 2019, l'attestation
 * d'assurance dans un classeur chez le trésorier, l'arrêté de la mairie dans
 * un dossier « scans » sur un ordinateur qui a changé depuis. Ces pièces
 * existaient, mais nulle part — et un changement de bureau les perd toutes en
 * une fois.
 *
 * L'échéance est la seule chose de cet écran qui parle d'elle-même. Une
 * assurance expirée ne prévient pas, et une buvette non assurée un soir de
 * fête engage des personnes, pas une structure : ce qui arrive à terme monte
 * donc en tête, avant les catégories et avant la liste.
 */
export default function DocumentsPage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const [categorie, setCategorie] = useState<string>('');
  const [ouvert, setOuvert] = useState(false);
  const [notice, setNotice] = useState<{ type: 'ok' | 'erreur'; texte: string } | null>(null);

  const peutVoir = aLeDroit('voir_documents');
  const peutGerer = aLeDroit('gerer_documents');

  const { data, isPending } = useQuery({
    queryKey: ['documents'],
    queryFn: fetchDocuments,
    enabled: peutVoir,
  });

  const supprimer = useMutation({
    mutationFn: (id: number) => supprimerDocument(id),
    onSuccess: () => {
      setNotice({ type: 'ok', texte: 'Document retiré.' });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  // La pièce dont on corrige la fiche, s'il y en a une.
  const [aModifier, setAModifier] = useState<DocumentAssociation | null>(null);

  const modifier = useMutation({
    mutationFn: (payload: Record<string, unknown>) => modifierDocument(aModifier!.id, payload),
    onSuccess: () => {
      setNotice({ type: 'ok', texte: 'Fiche corrigée.' });
      setAModifier(null);
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      // L'alerte d'échéance de l'accès rapide lit les mêmes dates.
      queryClient.invalidateQueries({ queryKey: ['documents-echeances'] });
    },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  const documents = data?.documents ?? vide<any>();
  const categories = data?.categories ?? {};

  const visibles = useMemo(
    () => (categorie === '' ? documents : documents.filter((d) => d.categorie === categorie)),
    [documents, categorie]
  );

  // Ce qui arrive à terme, ou vient de le dépasser. Deux mois d'avance :
  // c'est le délai qu'il faut pour relancer un assureur qui ne répond pas.
  const alertes = useMemo(
    () =>
      documents
        .filter((d) => d.joursAvantEcheance !== null && d.joursAvantEcheance <= 60)
        .sort((a, b) => (a.joursAvantEcheance ?? 0) - (b.joursAvantEcheance ?? 0)),
    [documents]
  );

  if (authLoading || (peutVoir && isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!peutVoir) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              La bibliothèque dépend de votre fonction au bureau : un administrateur peut l'ouvrir
              dans la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <FolderOpen className="w-7 h-7 text-primary-700 shrink-0" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Documents
            </h1>
            <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
              Statuts, règlement, assurances, contrats et autorisations — au même endroit, et qui
              survivent au changement de bureau.
            </p>
          </div>
        </div>
        {peutGerer && (
          <button type="button" onClick={() => setOuvert(!ouvert)} className="btn-primary inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Déposer
          </button>
        )}
      </div>

      {notice && (
        <div
          className={`card text-sm flex items-start gap-3 ${
            notice.type === 'ok'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="flex-1">{notice.texte}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      {/* Ce qui arrive à terme passe devant tout le reste. */}
      {alertes.length > 0 && (
        <div className="card bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 space-y-2">
          <p className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0" /> À renouveler
          </p>
          <ul className="text-sm text-amber-900 dark:text-amber-200 space-y-1">
            {alertes.map((d) => (
              <li key={d.id}>
                <strong>{d.titre}</strong> —{' '}
                {(d.joursAvantEcheance ?? 0) < 0
                  ? `expiré depuis ${Math.abs(d.joursAvantEcheance ?? 0)} jour${Math.abs(d.joursAvantEcheance ?? 0) > 1 ? 's' : ''}`
                  : (d.joursAvantEcheance ?? 0) === 0
                    ? "expire aujourd'hui"
                    : `expire dans ${d.joursAvantEcheance} jour${(d.joursAvantEcheance ?? 0) > 1 ? 's' : ''}`}
                {d.echeance ? ` (${jourCourt.format(new Date(d.echeance))})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {ouvert && peutGerer && (
        <Depot
          categories={categories}
          categoriesAEcheance={data?.categoriesAEcheance ?? []}
          tailleMax={data?.tailleMax ?? 0}
          onFini={(message) => {
            setNotice({ type: 'ok', texte: message });
            setOuvert(false);
            queryClient.invalidateQueries({ queryKey: ['documents'] });
          }}
          onEchec={(message) => setNotice({ type: 'erreur', texte: message })}
        />
      )}

      <div className="flex flex-wrap gap-2">
        <Filtre actif={categorie === ''} onClick={() => setCategorie('')}>
          Tout ({documents.length})
        </Filtre>
        {Object.entries(categories).map(([code, label]) => {
          const nb = documents.filter((d) => d.categorie === code).length;
          if (nb === 0) return null;
          return (
            <Filtre key={code} actif={categorie === code} onClick={() => setCategorie(code)}>
              {label} ({nb})
            </Filtre>
          );
        })}
      </div>

      {visibles.length === 0 ? (
        <div className="card text-center py-10 space-y-2">
          <FileText className="w-8 h-8 mx-auto text-gray-300" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {documents.length === 0
              ? "La bibliothèque est vide. Les statuts et l'attestation d'assurance sont les deux premières pièces à y mettre."
              : 'Aucun document dans cette catégorie.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {visibles.map((d) => (
            <Ligne
              key={d.id}
              document={d}
              peutGerer={peutGerer}
              onModifier={() => setAModifier(d)}
              onSupprimer={() => {
                if (window.confirm(`Retirer « ${d.titre} » de la bibliothèque ?`)) {
                  supprimer.mutate(d.id);
                }
              }}
            />
          ))}
        </ul>
      )}

      {aModifier && (
        <CorrigerFiche
          document={aModifier}
          categories={categories}
          categoriesAEcheance={data?.categoriesAEcheance ?? []}
          enCours={modifier.isPending}
          onFermer={() => setAModifier(null)}
          onEnregistrer={(payload) => modifier.mutate(payload)}
        />
      )}
    </div>
  );
}

/**
 * Corriger la fiche d'une pièce déjà déposée.
 *
 * Le fichier ne se remplace pas ici, et c'est délibéré : une attestation
 * d'assurance de l'an dernier reste ce qu'elle est, et lui substituer celle de
 * cette année sous le même titre effacerait la trace de la première. Une
 * nouvelle version se dépose comme une nouvelle pièce.
 *
 * Ce qui se corrige, c'est ce qu'on a saisi de travers : un titre, une
 * catégorie, une date. L'échéance surtout — saisie à côté, l'alerte tombe au
 * mauvais moment pour toujours, et la seule issue était de retirer la pièce et
 * de la remettre, geste que personne ne fait.
 */
function CorrigerFiche({
  document: d,
  categories,
  categoriesAEcheance,
  enCours,
  onFermer,
  onEnregistrer,
}: {
  document: DocumentAssociation;
  categories: Record<string, string>;
  categoriesAEcheance: string[];
  enCours: boolean;
  onFermer: () => void;
  onEnregistrer: (payload: Record<string, unknown>) => void;
}) {
  const [titre, setTitre] = useState(d.titre);
  const [categorie, setCategorie] = useState(d.categorie);
  const [description, setDescription] = useState(d.description ?? '');
  const [dateDocument, setDateDocument] = useState((d.dateDocument ?? '').slice(0, 10));
  const [echeance, setEcheance] = useState((d.echeance ?? '').slice(0, 10));

  const attendEcheance = categoriesAEcheance.includes(categorie);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 space-y-3">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Corriger la fiche</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Le fichier lui-même n'est pas remplacé : une nouvelle version se dépose comme une
            nouvelle pièce, pour que l'ancienne reste consultable.
          </p>

          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">Titre</span>
            <input className="input mt-1" value={titre} onChange={(e) => setTitre(e.target.value)} />
          </label>

          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">Catégorie</span>
            <select
              className="input mt-1"
              value={categorie}
              onChange={(e) => setCategorie(e.target.value)}
            >
              {Object.entries(categories).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="text-gray-700 dark:text-gray-300">Date du document</span>
              <input
                type="date"
                className="input mt-1"
                value={dateDocument}
                onChange={(e) => setDateDocument(e.target.value)}
              />
            </label>

            <label className="block text-sm">
              <span className="text-gray-700 dark:text-gray-300">
                Valable jusqu'au
                {attendEcheance && <span className="text-amber-600"> · attendu</span>}
              </span>
              <input
                type="date"
                className="input mt-1"
                value={echeance}
                onChange={(e) => setEcheance(e.target.value)}
              />
            </label>
          </div>

          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">Description</span>
            <textarea
              className="input mt-1"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <div className="flex flex-wrap gap-2 justify-end pt-1">
            <button type="button" className="btn-outline text-sm" onClick={onFermer}>
              Annuler
            </button>
            <button
              type="button"
              className="btn-primary text-sm inline-flex items-center gap-2"
              disabled={enCours || titre.trim() === ''}
              onClick={() =>
                onEnregistrer({
                  titre: titre.trim(),
                  categorie,
                  description: description.trim() || null,
                  dateDocument: dateDocument || null,
                  echeance: echeance || null,
                })
              }
            >
              {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Filtre({
  actif,
  onClick,
  children,
}: {
  actif: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
        actif
          ? 'bg-primary-600 border-primary-600 text-white'
          : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary-300'
      }`}
    >
      {children}
    </button>
  );
}

function Ligne({
  document: d,
  peutGerer,
  onSupprimer,
  onModifier,
}: {
  document: DocumentAssociation;
  peutGerer: boolean;
  onSupprimer: () => void;
  onModifier: () => void;
}) {
  const perime = d.joursAvantEcheance !== null && d.joursAvantEcheance < 0;

  return (
    <li className="card flex flex-wrap items-center gap-x-4 gap-y-2">
      <FileText className={`w-5 h-5 shrink-0 ${perime ? 'text-red-500' : 'text-primary-600'}`} />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900 dark:text-gray-100">{d.titre}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {d.categorieLabel}
          {d.dateDocument ? ` · du ${jourCourt.format(new Date(d.dateDocument))}` : ''}
          {d.echeance ? ` · valable jusqu'au ${jourCourt.format(new Date(d.echeance))}` : ''}
          {' · '}
          {poids(d.taille)}
          {d.deposePar ? ` · déposé par ${d.deposePar}` : ''}
        </p>
        {d.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{d.description}</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Le fichier passe par l'API : déposé en clair dans le site, il
            serait lisible de quiconque connaît son adresse. */}
        <a
          href={`/api/documents/${d.id}/fichier`}
          target="_blank"
          rel="noopener"
          className="btn-outline inline-flex items-center gap-2 text-sm py-1.5"
        >
          <Download className="w-4 h-4" /> Ouvrir
        </a>
        {peutGerer && (
          <>
            {/* Corriger sa fiche plutôt que le redéposer. Une date d'échéance
                saisie de travers obligeait à retirer la pièce et à la
                remettre — geste que personne ne fait, de sorte que l'alerte
                d'échéance tombait au mauvais moment pour toujours. */}
            <button
              type="button"
              onClick={onModifier}
              title="Corriger la fiche"
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onSupprimer}
              title="Retirer de la bibliothèque"
              className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </li>
  );
}

/**
 * Le dépôt d'une pièce.
 *
 * L'échéance n'est réclamée nulle part — un contrat sans date de fin existe —
 * mais elle est mise en avant sur les catégories où on l'oublie.
 */
function Depot({
  categories,
  categoriesAEcheance,
  tailleMax,
  onFini,
  onEchec,
}: {
  categories: Record<string, string>;
  categoriesAEcheance: string[];
  tailleMax: number;
  onFini: (message: string) => void;
  onEchec: (message: string) => void;
}) {
  const champ = useRef<HTMLInputElement>(null);
  const [fichier, setFichier] = useState<File | null>(null);
  const [categorie, setCategorie] = useState('statuts');
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [dateDocument, setDateDocument] = useState('');
  const [echeance, setEcheance] = useState('');
  const [enCours, setEnCours] = useState(false);

  const attendEcheance = categoriesAEcheance.includes(categorie);

  const envoyer = async () => {
    if (!fichier) return;
    setEnCours(true);
    try {
      const corps = new FormData();
      corps.append('document', fichier);
      corps.append('categorie', categorie);
      corps.append('titre', titre);
      corps.append('description', description);
      corps.append('dateDocument', dateDocument);
      corps.append('echeance', echeance);
      await deposerDocument(corps);
      onFini('Document déposé.');
    } catch (e) {
      onEchec((e as Error).message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="card space-y-3">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100">Déposer un document</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400">
          Nature
          <select className="input mt-1" value={categorie} onChange={(e) => setCategorie(e.target.value)}>
            {Object.entries(categories).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Titre
          <input
            className="input mt-1"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            placeholder="Statuts déposés en préfecture"
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Date du document
          <input
            type="date"
            className="input mt-1"
            value={dateDocument}
            onChange={(e) => setDateDocument(e.target.value)}
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Valable jusqu'au
          {attendEcheance && <span className="text-amber-600"> — à renseigner</span>}
          <input
            type="date"
            className="input mt-1"
            value={echeance}
            onChange={(e) => setEcheance(e.target.value)}
          />
        </label>

        <textarea
          className="input lg:col-span-2 min-h-[3.5rem]"
          placeholder="Précision (facultatif) : numéro de contrat, interlocuteur, ce qu'il faut savoir avant de le rouvrir"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={champ}
          type="file"
          accept=".pdf,.doc,.docx,.odt,.png,.jpg,.jpeg,.webp"
          className="hidden"
          onChange={(e) => setFichier(e.target.files?.[0] ?? null)}
        />
        <button type="button" onClick={() => champ.current?.click()} className="btn-outline inline-flex items-center gap-2">
          <Upload className="w-4 h-4" />
          {fichier ? fichier.name : 'Choisir le fichier'}
        </button>
        <button
          type="button"
          onClick={() => void envoyer()}
          disabled={!fichier || enCours}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
          Déposer
        </button>
      </div>

      <p className="text-xs text-gray-400">
        PDF, DOC, DOCX, ODT, PNG, JPG ou WEBP, {Math.round(tailleMax / (1024 * 1024))} Mo au plus.
        La photo d'une attestation prise au téléphone convient — c'est souvent tout ce dont on
        dispose.
      </p>
    </div>
  );
}
